import {Accordion, Badge, Button, Pagination} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {ThemeIcon} from 'src/design/atoms/ThemeIcon';
import React, {useState} from 'react';
import {Link} from 'react-router-dom';

import {
  ModuleEnum,
  NexusNodeType,
  WorkflowStateEnum,
} from 'src/api/ProactApiClient';
import {ChevronRightIcon, IconSize} from 'src/design/atoms/icons/CommonIcons';
import {
  useGetWorkflowListQuery,
  useGetWorkflowQuery,
} from 'src/redux/queries/workflow';
import {useModulePermissions} from 'src/shared/auth/permissions/useModulePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {WorkflowTimelineCompact} from './WorkflowTimelineCompact';
import {getStateBadgeColor, workflowEntityConfig} from './workflowDesignSystem';

const maxPageRows = 5;

export interface ViewLinkedWorkflowSectionProps {
  formId?: number;
  taskAssignmentId?: number;
  taskDefinitionId?: number;
  courseAssignmentId?: number;
  courseSessionId?: number;
  itemAssignmentId?: number;
}

function buildHighlightEntityId(
  props: ViewLinkedWorkflowSectionProps
): {type: string; id: number} | undefined {
  if (props.formId) return {type: 'form', id: props.formId};
  if (props.taskDefinitionId) return {type: 'task', id: props.taskDefinitionId};
  if (props.taskAssignmentId)
    return {type: 'taskAssignment', id: props.taskAssignmentId};
  if (props.courseSessionId)
    return {type: 'trainingSession', id: props.courseSessionId};
  if (props.itemAssignmentId)
    return {type: 'itemAssignment', id: props.itemAssignmentId};
  return undefined;
}

export const ViewLinkedWorkflowSection: React.FC<
  ViewLinkedWorkflowSectionProps
> = props => {
  const {
    formId,
    taskAssignmentId,
    taskDefinitionId,
    itemAssignmentId,
    courseSessionId,
  } = props;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(
    null
  );

  const perms = useModulePermissions(ModuleEnum.Workflows);

  const skipList =
    (!formId &&
      !taskAssignmentId &&
      !taskDefinitionId &&
      !itemAssignmentId &&
      !courseSessionId) ||
    !perms.view;

  const {
    data,
    isLoading: isWorkflowsLoading,
    isFetching: isWorkflowsFetching,
  } = useGetWorkflowListQuery(
    {
      start: (currentPage - 1) * maxPageRows,
      length: maxPageRows,
      formId,
      taskAssignmentId,
      taskDefinitionId,
      itemAssignmentId,
      courseSessionId,
    },
    {skip: skipList}
  );
  const workflows = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;

  const workflowId = selectedWorkflowId
    ? Number(selectedWorkflowId)
    : workflows.length > 0
    ? workflows[0]?.id
    : undefined;

  const {
    data: selectedWorkflow,
    isLoading: isWorkflowLoading,
    isFetching: isWorkflowFetching,
  } = useGetWorkflowQuery(Number(workflowId), {
    skip: !workflowId,
  });

  const isLoading = isWorkflowsFetching || isWorkflowsLoading;
  const isSelectedLoading = isWorkflowLoading || isWorkflowFetching;

  const highlightEntityId = buildHighlightEntityId(props);

  const workflowConfig = workflowEntityConfig[NexusNodeType.Workflow];
  const WorkflowIcon = workflowConfig.icon;

  if (skipList || workflows.length === 0) return null;

  return (
    <Paper withBorder p="md">
      <Stack gap="md">
        <Text fw={600} size="lg">
          Related Workflows
        </Text>

        {isLoading ? (
          <Group justify="center" py="md">
            <Loader size="sm" />
          </Group>
        ) : (
          <>
            <Accordion
              defaultValue={String(workflows[0]?.id)}
              onChange={value => setSelectedWorkflowId(value)}>
              {workflows.map(workflow => {
                const stateName =
                  WorkflowStateEnum[workflow.state] ?? String(workflow.state);
                return (
                  <Accordion.Item key={workflow.id} value={String(workflow.id)}>
                    <Accordion.Control>
                      <Group gap="xs">
                        <ThemeIcon
                          size={20}
                          variant="light"
                          color={workflowConfig.color}
                          radius="xl">
                          <WorkflowIcon size={12} />
                        </ThemeIcon>
                        <Text size="sm">
                          {workflow.workflowDefinition?.name ?? 'Workflow'}
                        </Text>
                        <Badge
                          size="xs"
                          variant="light"
                          color={getStateBadgeColor(
                            stateName,
                            workflowConfig.color
                          )}>
                          {stateName}
                        </Badge>
                      </Group>
                    </Accordion.Control>
                    <Accordion.Panel>
                      {isSelectedLoading || !selectedWorkflow ? (
                        <Group justify="center" py="sm">
                          <Loader size="sm" />
                        </Group>
                      ) : (
                        <Stack gap="xs">
                          <Group justify="flex-end">
                            <Button
                              component={Link}
                              to={PathUtility.WorkflowView(
                                String(selectedWorkflow.workflowDefinitionId),
                                String(selectedWorkflow.id)
                              )}
                              target="_blank"
                              size="sm"
                              variant="subtle"
                              rightSection={
                                <ChevronRightIcon size={IconSize.sm} />
                              }>
                              View Workflow
                            </Button>
                          </Group>
                          <WorkflowTimelineCompact
                            workflow={selectedWorkflow}
                            compact
                            highlightEntityId={highlightEntityId}
                          />
                        </Stack>
                      )}
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>

            {totalRecords > maxPageRows && (
              <Pagination
                total={Math.ceil(totalRecords / maxPageRows)}
                value={currentPage}
                onChange={setCurrentPage}
              />
            )}
          </>
        )}
      </Stack>
    </Paper>
  );
};
